import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  getOnboarding,
  resubmitPortalKyc,
  updateOnboarding,
  uploadPortalKycDocument,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"

function formatDateTime(value?: string | null) {
  if (!value) return "-"
  return new Date(value).toLocaleString()
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const onboardingQuery = useQuery({
    queryKey: ["portal-onboarding"],
    queryFn: () => getOnboarding().then((response) => response.data),
  })
  const [fullName, setFullName] = useState("")
  const [dateOfBirth, setDateOfBirth] = useState("")
  const [gender, setGender] = useState<"MALE" | "FEMALE" | "OTHER">("MALE")
  const [altPhone, setAltPhone] = useState("")
  const [physicalAddress, setPhysicalAddress] = useState("")
  const [nationalId, setNationalId] = useState("")
  const [preferredLanguage, setPreferredLanguage] = useState<"en" | "sn">("en")
  const [nextOfKinName, setNextOfKinName] = useState("")
  const [nextOfKinRelationship, setNextOfKinRelationship] = useState("")
  const [nextOfKinPhone, setNextOfKinPhone] = useState("+263")
  const [documentType, setDocumentType] = useState("NATIONAL_ID_FRONT")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [requestFiles, setRequestFiles] = useState<Record<string, File | null>>({})
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    if (!onboardingQuery.data) return
    setFullName(onboardingQuery.data.fullName ?? "")
    setDateOfBirth(onboardingQuery.data.dateOfBirth ?? "")
    setGender((onboardingQuery.data.gender as "MALE" | "FEMALE" | "OTHER") ?? "MALE")
    setAltPhone(onboardingQuery.data.altPhone ?? "")
    setPhysicalAddress(onboardingQuery.data.physicalAddress ?? "")
    setNationalId(onboardingQuery.data.nationalId ?? "")
    setPreferredLanguage(onboardingQuery.data.preferredLanguage ?? "en")
    setNextOfKinName(onboardingQuery.data.nextOfKin?.fullName ?? "")
    setNextOfKinRelationship(onboardingQuery.data.nextOfKin?.relationship ?? "")
    setNextOfKinPhone(onboardingQuery.data.nextOfKin?.phoneNumber ?? "+263")
  }, [onboardingQuery.data])

  const saveMutation = useMutation({
    mutationFn: () =>
      updateOnboarding({
        fullName,
        dateOfBirth,
        gender,
        altPhone: altPhone || null,
        physicalAddress,
        nationalId,
        preferredLanguage,
        nextOfKin: {
          fullName: nextOfKinName,
          relationship: nextOfKinRelationship,
          phoneNumber: nextOfKinPhone,
        },
      }),
    onSuccess: async () => {
      setFeedback("Onboarding details saved.")
      setError("")
      await queryClient.invalidateQueries({ queryKey: ["portal-onboarding"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-profile"] })
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message)
      setFeedback("")
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (payload: {
      documentType: string
      file: File | null
      requestItemId?: string | null
    }) => {
      if (!payload.file) {
        throw new Error("Choose a document before uploading.")
      }

      return uploadPortalKycDocument(payload.documentType, payload.file, payload.requestItemId)
    },
    onSuccess: async (_response, variables) => {
      setFeedback("Document uploaded.")
      setError("")
      setSelectedFile(null)
      if (variables.requestItemId) {
        setRequestFiles((current) => ({ ...current, [variables.requestItemId!]: null }))
      }
      await queryClient.invalidateQueries({ queryKey: ["portal-onboarding"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-profile"] })
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message)
      setFeedback("")
    },
  })

  const resubmitMutation = useMutation({
    mutationFn: () => resubmitPortalKyc(),
    onSuccess: async () => {
      setFeedback("KYC resubmitted for review.")
      setError("")
      await queryClient.invalidateQueries({ queryKey: ["portal-onboarding"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-profile"] })
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message)
      setFeedback("")
    },
  })

  if (onboardingQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={220} />
        <Skeleton height={260} />
      </div>
    )
  }

  const onboarding = onboardingQuery.data
  if (!onboarding) return null

  const activeRequest = onboarding.activeKycRequest
  const requestedDocumentItems =
    activeRequest?.items.filter((item) => item.itemKind === "DOCUMENT") ?? []
  const allRequestedItemsAnswered = activeRequest
    ? activeRequest.items.every((item) => item.responseStatus === "RESPONDED")
    : false
  const canEditKyc = onboarding.kycStatus !== "REJECTED"

  function getRequestedItem(itemKind: string, itemKey: string) {
    return activeRequest?.items.find(
      (item) => item.itemKind === itemKind && item.itemKey === itemKey,
    )
  }

  function renderFieldHint(itemKind: string, itemKey: string) {
    const requestItem = getRequestedItem(itemKind, itemKey)
    if (!requestItem) return null

    return (
      <div className="hint-copy">
        <strong>{requestItem.label} requested.</strong> {requestItem.instructions}
        {requestItem.responseStatus === "RESPONDED"
          ? " Your latest saved value has been recorded."
          : ""}
      </div>
    )
  }

  return (
    <div className="page-stack">
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Borrower onboarding</p>
            <h3>Profile and KYC</h3>
          </div>
        </div>
        {feedback ? <div className="status-banner success">{feedback}</div> : null}
        {error ? <div className="status-banner error">{error}</div> : null}
        {onboarding.kycStatus === "ACTION_REQUIRED" && activeRequest ? (
          <div className="hint-copy">
            <strong>More information is needed.</strong> {activeRequest.message}
            <div className="button-row" style={{ marginTop: "0.8rem" }}>
              <button
                className="primary-button"
                disabled={!allRequestedItemsAnswered || resubmitMutation.isPending}
                onClick={() => resubmitMutation.mutate()}
                type="button"
              >
                {resubmitMutation.isPending ? "Resubmitting..." : "Resubmit KYC"}
              </button>
            </div>
          </div>
        ) : null}
        {onboarding.kycStatus === "REJECTED" ? (
          <div className="status-banner error">
            KYC was rejected.
            {onboarding.kycRejectionReason ? ` ${onboarding.kycRejectionReason}` : ""}
          </div>
        ) : null}
        <div className="metric-grid">
          <div>
            <dt>Profile</dt>
            <dd>{onboarding.isProfileComplete ? "Complete" : "Incomplete"}</dd>
          </div>
          <div>
            <dt>Required documents</dt>
            <dd>{onboarding.hasRequiredDocuments ? "Complete" : "Missing items"}</dd>
          </div>
        </div>
        {onboarding.missingDocumentTypes.length > 0 ? (
          <div className="hint-copy">
            Missing: {onboarding.missingDocumentTypes.join(", ").replaceAll("_", " ")}
          </div>
        ) : null}
        {activeRequest ? (
          <div className="card-list">
            {activeRequest.items.map((item) => (
              <div className="list-row" key={item.requestItemId}>
                <div>
                  <strong>{item.label}</strong>
                  <p className="subtle">{item.instructions}</p>
                </div>
                <strong>{item.responseStatus === "RESPONDED" ? "Updated" : "Pending"}</strong>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Identity details</p>
            <h3>Borrower profile</h3>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault()
            saveMutation.mutate()
          }}
        >
          <label>
            Full name
            {renderFieldHint("PROFILE_FIELD", "FULL_NAME")}
            <input onChange={(event) => setFullName(event.target.value)} value={fullName} />
          </label>
          <label>
            Date of birth
            {renderFieldHint("PROFILE_FIELD", "DATE_OF_BIRTH")}
            <input
              onChange={(event) => setDateOfBirth(event.target.value)}
              type="date"
              value={dateOfBirth}
            />
          </label>
          <label>
            Gender
            {renderFieldHint("PROFILE_FIELD", "GENDER")}
            <select onChange={(event) => setGender(event.target.value as any)} value={gender}>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <label>
            Alternative phone
            {renderFieldHint("PROFILE_FIELD", "ALT_PHONE")}
            <input onChange={(event) => setAltPhone(event.target.value)} value={altPhone} />
          </label>
          <label>
            Physical address
            {renderFieldHint("PROFILE_FIELD", "PHYSICAL_ADDRESS")}
            <textarea
              onChange={(event) => setPhysicalAddress(event.target.value)}
              rows={3}
              value={physicalAddress}
            />
          </label>
          <label>
            National ID
            {renderFieldHint("PROFILE_FIELD", "NATIONAL_ID")}
            <input onChange={(event) => setNationalId(event.target.value)} value={nationalId} />
          </label>
          <label>
            Preferred language
            <select
              onChange={(event) => setPreferredLanguage(event.target.value as "en" | "sn")}
              value={preferredLanguage}
            >
              <option value="en">English</option>
              <option value="sn">chiShona</option>
            </select>
          </label>
          <label>
            Next of kin name
            {renderFieldHint("NEXT_OF_KIN_FIELD", "FULL_NAME")}
            <input onChange={(event) => setNextOfKinName(event.target.value)} value={nextOfKinName} />
          </label>
          <label>
            Relationship
            {renderFieldHint("NEXT_OF_KIN_FIELD", "RELATIONSHIP")}
            <input
              onChange={(event) => setNextOfKinRelationship(event.target.value)}
              value={nextOfKinRelationship}
            />
          </label>
          <label>
            Next of kin phone
            {renderFieldHint("NEXT_OF_KIN_FIELD", "PHONE_NUMBER")}
            <input onChange={(event) => setNextOfKinPhone(event.target.value)} value={nextOfKinPhone} />
          </label>
          <button className="primary-button" disabled={saveMutation.isPending} type="submit">
            {saveMutation.isPending ? "Saving..." : "Save onboarding"}
          </button>
        </form>
      </section>

      {requestedDocumentItems.length > 0 ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Requested documents</p>
              <h3>Upload clearer or replacement files</h3>
            </div>
          </div>
          <div className="card-list">
            {requestedDocumentItems.map((item) => {
              const documentGroup = onboarding.documentGroups.find(
                (group) => group.documentType === item.itemKey,
              )

              return (
                <form
                  className="list-row"
                  key={item.requestItemId}
                  onSubmit={(event) => {
                    event.preventDefault()
                    uploadMutation.mutate({
                      documentType: item.itemKey,
                      file: requestFiles[item.requestItemId] ?? null,
                      requestItemId: item.requestItemId,
                    })
                  }}
                >
                  <div>
                    <strong>{item.label}</strong>
                    <p className="subtle">{item.instructions}</p>
                    <p className="subtle">
                      Latest upload: {documentGroup?.latestDocument?.fileName ?? "None yet"}
                    </p>
                  </div>
                  <div className="form-grid">
                    <input
                      onChange={(event) =>
                        setRequestFiles((current) => ({
                          ...current,
                          [item.requestItemId]: event.target.files?.[0] ?? null,
                        }))
                      }
                      type="file"
                    />
                    <button className="primary-button" disabled={uploadMutation.isPending} type="submit">
                      {uploadMutation.isPending ? "Uploading..." : item.responseStatus === "RESPONDED" ? "Upload newer version" : "Upload requested file"}
                    </button>
                  </div>
                </form>
              )
            })}
          </div>
          {activeRequest && !allRequestedItemsAnswered ? (
            <div className="hint-copy">
              Respond to every requested item, then use <strong>Resubmit KYC</strong>.
            </div>
          ) : null}
        </section>
      ) : null}

      {canEditKyc ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">{activeRequest ? "Additional documents" : "KYC documents"}</p>
              <h3>{activeRequest ? "Upload extra or optional files" : "Upload required files"}</h3>
            </div>
          </div>
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              uploadMutation.mutate({
                documentType,
                file: selectedFile,
                requestItemId: null,
              })
            }}
          >
            <label>
              Document type
              <select onChange={(event) => setDocumentType(event.target.value)} value={documentType}>
                {[...onboarding.requiredDocumentTypes, ...onboarding.optionalDocumentTypes].map((type) => (
                  <option key={type} value={type}>
                    {type.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <label>
              File
              <input
                onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
                type="file"
              />
            </label>
            <button className="primary-button" disabled={uploadMutation.isPending} type="submit">
              {uploadMutation.isPending ? "Uploading..." : "Upload document"}
            </button>
          </form>
        </section>
      ) : null}

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Document library</p>
            <h3>Latest uploads and history</h3>
          </div>
        </div>
        <div className="card-list">
          {onboarding.documentGroups.length === 0 ? (
            <p className="empty-copy">No documents uploaded yet.</p>
          ) : (
            onboarding.documentGroups.map((group) => (
              <div className="list-row" key={group.documentType}>
                <div>
                  <strong>{group.documentType.replaceAll("_", " ")}</strong>
                  <p className="subtle">
                    Latest: {group.latestDocument?.fileName ?? "No current upload"} · {group.totalUploads} upload
                    {group.totalUploads === 1 ? "" : "s"}
                  </p>
                  {group.history.length > 0 ? (
                    <details style={{ marginTop: "0.75rem" }}>
                      <summary>View upload history ({group.history.length})</summary>
                      <div className="card-list" style={{ marginTop: "0.75rem" }}>
                        {group.history.map((document) => (
                          <div className="list-row" key={document.documentId}>
                            <div>
                              <strong>{document.fileName}</strong>
                              <p className="subtle">{formatDateTime(document.createdAt)}</p>
                            </div>
                            <a
                              className="ghost-button"
                              href={document.downloadPath}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          </div>
                        ))}
                      </div>
                    </details>
                  ) : null}
                </div>
                {group.latestDocument ? (
                  <a
                    className="ghost-button"
                    href={group.latestDocument.downloadPath}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View latest
                  </a>
                ) : null}
              </div>
            ))
          )}
        </div>
      </section>

      <section className="card">
        <div className="button-row">
          {onboarding.portalStandardProductsEnabled || onboarding.application ? (
            <button className="primary-button" onClick={() => navigate("/apply")} type="button">
              {onboarding.application ? "View standard application" : "Other loan products"}
            </button>
          ) : (
            <button className="primary-button" onClick={() => navigate("/")} type="button">
              Back to dashboard
            </button>
          )}
          <Link className="ghost-button" to="/">
            Back to home
          </Link>
        </div>
      </section>
    </div>
  )
}
